import {
  BoardState,
  Color,
  PieceType,
  GameStatus,
  VARIANT_CONFIGS,
} from '@draughts/shared';
import { countPieces } from '../board/board-utils.js';
import { squareToPosition } from '../board/square-utils.js';
import { willPromote } from '../moves/simple-moves.js';
import { getAllLegalMoves } from '../moves/capture-moves.js';
import { determineGameStatus } from './move-execution.js';

/**
 * Perché una posizione composta a mano nell'EDITOR non è valida.
 * Ordinati dal problema più strutturale al più "di gioco".
 */
export type EditorSetupError =
  | 'no_white'                // il Bianco non ha pezzi
  | 'no_black'                // il Nero non ha pezzi
  | 'off_board'               // un pezzo su una casella che questa variante non ha
  | 'duplicate_square'        // due pezzi sulla stessa casella
  | 'man_on_promotion_row'    // una pedina è sulla propria riga di promozione (avrebbe dovuto promuovere)
  | 'too_many_pieces'         // più pezzi del massimo iniziale della variante
  | 'side_to_move_no_moves'   // il giocatore di turno non ha mosse legali
  | 'already_terminal';       // la posizione è già vinta/patta

export interface SetupValidation {
  valid: boolean;
  reason?: EditorSetupError;
}

/**
 * Valida una posizione costruita nell'editor.
 *
 * Sorgente UNICA condivisa da client (editor-store) e server (challenge.handler),
 * così non possono divergere: il client la usa per abilitare/disabilitare le CTA e
 * mostrare il motivo, il server la usa come gate autoritativo su `challenge:send`.
 *
 * Due livelli di severità:
 * - `requirePlayable = false` ("analizzabile"): solo controlli strutturali (entrambi i
 *   lati hanno pezzi, nessuna pedina sulla riga di promozione, entro il tetto di pezzi).
 *   Va bene per la sola analisi, dove anche una posizione già "finita" è interessante.
 * - `requirePlayable = true` ("giocabile/sfidabile", default): oltre allo strutturale, il
 *   giocatore di turno deve avere almeno una mossa legale e la posizione non deve essere
 *   già terminale. È ciò che serve per Gioca-vs-AI e per una sfida online reale.
 */
export function validateEditablePosition(
  board: BoardState,
  requirePlayable = true,
): SetupValidation {
  const config = VARIANT_CONFIGS[board.variant];
  const boardSize = config.size;
  const darkSquareBottomRight = config.darkSquareBottomRight;
  const allSquares = config.playableSquares === boardSize * boardSize;

  /*
   * I due controlli STRUTTURALI che mancavano, e che vengono prima di tutto:
   * senza di loro nemmeno `squareToPosition` più sotto è chiamabile in sicurezza.
   *
   * 1. CASELLA FUORI DALLA DAMIERA. `fen-parser` accetta qualunque numero ≥ 1
   *    (`if (isNaN(square) || square < 1) throw` — nessun tetto superiore), e a
   *    valle `squareToPosition` LANCIA su una casella fuori range. Un validatore
   *    che solleva invece di rispondere `{valid:false}` costringe ogni chiamante a
   *    un try/catch, e i due che ce l'hanno lo dichiarano nel commento: qui la
   *    domanda riceve una risposta.
   *
   * 2. DUE PEZZI SULLA STESSA CASELLA. `fen-parser` fa `pieces.push(...)` senza
   *    controllo di unicità, e nessuno a valle se ne accorge: `getPieceAt`
   *    restituisce il primo, `setPieceAt` sostituisce solo il `findIndex`, e
   *    `removePieceAt` — che filtra per casella — li toglie TUTTI E DUE. Una
   *    posizione così passava la validazione e si poteva giocare: il pezzo di
   *    sotto spariva alla prima cattura su quella casella, con `move.captures`
   *    vuoto, quindi senza lasciare traccia nella revisione.
   *
   * I due `try/catch` che i chiamanti hanno oggi (`challenge-settings`,
   * `editor.controller`) restano utili comunque: `fenToBoardState` continua a
   * lanciare sui token malformati, che è un'altra cosa.
   */
  const caselleViste = new Set<number>();
  for (const piece of board.pieces) {
    if (piece.square < 1 || piece.square > config.playableSquares) {
      return { valid: false, reason: 'off_board' };
    }
    if (caselleViste.has(piece.square)) {
      return { valid: false, reason: 'duplicate_square' };
    }
    caselleViste.add(piece.square);
  }

  const whiteCount = countPieces(board, Color.WHITE);
  const blackCount = countPieces(board, Color.BLACK);

  if (whiteCount === 0) return { valid: false, reason: 'no_white' };
  if (blackCount === 0) return { valid: false, reason: 'no_black' };

  // Nessuna pedina può stare sulla propria riga di promozione: nel gioco reale
  // avrebbe già promosso. `willPromote` è la stessa funzione usata dalla generazione
  // mosse, quindi il criterio coincide esattamente con quello di gioco.
  for (const piece of board.pieces) {
    if (piece.type !== PieceType.MAN) continue;
    const pos = squareToPosition(piece.square, boardSize, darkSquareBottomRight, allSquares);
    if (willPromote(piece.color, pos.row, boardSize)) {
      return { valid: false, reason: 'man_on_promotion_row' };
    }
  }

  // Nessun lato può avere più pezzi del massimo iniziale: catture e promozioni non
  // aumentano mai il numero di pezzi, quindi è un limite superiore reale.
  if (whiteCount > config.piecesPerPlayer || blackCount > config.piecesPerPlayer) {
    return { valid: false, reason: 'too_many_pieces' };
  }

  if (requirePlayable) {
    if (getAllLegalMoves(board).length === 0) {
      return { valid: false, reason: 'side_to_move_no_moves' };
    }
    if (determineGameStatus(board) !== GameStatus.PLAYING) {
      return { valid: false, reason: 'already_terminal' };
    }
  }

  return { valid: true };
}
